import { useCallback, useEffect, useState } from 'react'
import apiClient from '../api/client'
import { errorMessage } from '../api/ApiError'
import CardGrid, { type CardItem } from '../components/CardGrid'
import ErrorAlert from '../components/ErrorAlert'
import LoadingSpinner from '../components/LoadingSpinner'
import PrimerosMensajes from '../components/PrimerosMensajes'

/**
 * "Plantillas y speech" — la pantalla de consulta del asesor.
 *
 * Es de solo lectura a propósito: el asesor usa las plantillas y necesita
 * tener a mano cómo saluda el agente. «Speech vigente» leía `SPEECH_*` de la
 * config, claves que no escribe nadie, y decía siempre «No hay un speech
 * publicado… el agente usa su prompt por defecto» (13-oct-2026): hoy muestra
 * los primeros mensajes reales, los mismos de Agente IA › Speech.
 */

interface KnowledgeItem {
  id: string
  title: string
  content?: string
  type: string
  status?: string
  updated_at?: string
}

export default function Templates() {
  const [plantillas, setPlantillas] = useState<KnowledgeItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await apiClient.get<{ knowledge: KnowledgeItem[] }>(
        '/knowledge?type=TEMPLATE&limit=50',
      )
      setPlantillas(res.data.knowledge ?? [])
    } catch (err) {
      setError(errorMessage(err, 'No se pudieron cargar las plantillas.'))
    }

    setLoading(false)
  }, [])

  useEffect(() => { void fetch() }, [fetch])

  if (loading) return <LoadingSpinner label="Cargando plantillas…" />

  const items: CardItem[] = plantillas.map((t) => ({
    key: t.id,
    title: t.title || 'Plantilla sin título',
    subtitle: t.type,
    status: t.status ? <span className="badge-green">{t.status}</span> : undefined,
    body: t.content || 'Sin cuerpo definido.',
    meta: t.updated_at
      ? [{ label: 'Actualizada', value: new Date(t.updated_at).toLocaleDateString('es-PE') }]
      : undefined,
    actions: t.content ? (
      <button
        className="btn-row"
        onClick={() => void navigator.clipboard?.writeText(t.content ?? '')}
        title="Copiar el cuerpo para pegarlo en el compositor"
      >
        Copiar texto
      </button>
    ) : undefined,
  }))

  return (
    <div className="flex flex-col gap-5">
      {error && <ErrorAlert message={error} onRetry={() => void fetch()} />}

      <section>
        <h2 className="section-title mb-3">Plantillas aprobadas</h2>
        <CardGrid
          items={items}
          emptyTitle="Sin plantillas cargadas"
          emptyMessage="Un admin las carga desde Agente IA › Conocimiento, con tipo TEMPLATE."
        />
      </section>

      <section>
        <h2 className="section-title mb-1">Cómo saluda el agente</h2>
        <p className="mb-3 text-micro text-ink-muted">
          Lo primero que lee el cliente según por dónde entra, tal como lo manda el bot.
        </p>
        <PrimerosMensajes />
      </section>
    </div>
  )
}
